import styled from '@emotion/styled';
import { getFloatingHeaderHeight } from '@silvertours/front-features';
import { Theme } from '@silvertours/front-shared';

/*
 * The part of the search mask below the green area, as in Figma:
 * xs 69px, m 150px, xl 118px. The green ends there, so the edge follows the
 * search mask when the content above it changes (for example a promotion).
 */
const searchOverlap = { xs: '4.3125rem', md: '9.375rem', lg: '7.375rem' };

/* The reference heights of the darken gradient in Figma: xs 435px, m/xl 575px. */
const gradientHeight = { xs: '27.1875rem', md: '35.9375rem' };

/*
 * The darken gradient from the Figma design. The stops are fixed lengths of
 * the reference height. The green area cuts the gradient where it ends.
 */
const darkenGradient = (reference: string) => `linear-gradient(
  180deg,
  rgb(30 16 0 / 30%) calc(${reference} * 0.0016),
  rgb(0 0 0 / 18%) calc(${reference} * 0.2959),
  rgb(0 0 0 / 3%) calc(${reference} * 0.5438)
)`;

/**
 * The stage of the variant. It moves up behind the floating header, so one
 * background with one gradient fills the header area and the stage.
 * The search mask reaches below the green area (see `SearchArea`).
 */
const GreenStage = styled.div`
  --header-height: ${({ theme }) => getFloatingHeaderHeight(theme).base};

  /* Contains the negative margin of the search mask (no margin collapse). */
  display: flow-root;
  margin-top: calc(-1 * var(--header-height));
  padding: calc(var(--header-height) + ${({ theme }) => theme.spacing[20]})
    ${({ theme }) => theme.spacing[40]} 0;
  background-color: ${({ theme }) => theme.colors.brand.green100};
  background-image: ${darkenGradient(gradientHeight.xs)};
  background-repeat: no-repeat;
  background-position: top;
  background-size: 100% ${gradientHeight.xs};

  ${({ theme }) => theme.mq('md')} {
    --header-height: ${({ theme }) => getFloatingHeaderHeight(theme).md};

    padding-inline: ${({ theme }) => theme.spacing[60]};
    background-image: ${darkenGradient(gradientHeight.md)};
    background-size: 100% ${gradientHeight.md};
  }
`;

/** The content of the stage, as wide as the floating header bar. */
const StageContent = styled.div`
  position: relative;
  max-width: ${Theme.getBreakpoint('xl')}px;
  margin: ${({ theme }) => theme.spacing[30]} auto 0;
`;

/** The headline and, from xl, the trust seals at its right. */
const HeadlineRow = styled.div`
  position: relative;
`;

const Headline = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[20]};
  color: ${({ theme }) => theme.colors.neutral.white100};
  text-align: center;

  ${({ theme }) => theme.mq('md')} {
    text-align: left;
  }

  ${({ theme }) => theme.mq('lg')} {
    gap: ${({ theme }) => theme.spacing[10]};
  }
`;

/* Sizes of the design: xs 22/26, m 32/38, xl 48/58. */
const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.fontFamily.alternate};
  font-weight: ${({ theme }) => theme.fontWeight.heading};
  font-size: 1.375rem;
  line-height: 1.18;

  ${({ theme }) => theme.mq('md')} {
    font-size: ${({ theme }) => theme.fontSize.lg.size};
    line-height: ${({ theme }) => theme.fontSize.lg.lineHeight};
  }

  ${({ theme }) => theme.mq('lg')} {
    font-size: ${({ theme }) => theme.fontSize.xl.size};
    line-height: ${({ theme }) => theme.fontSize.xl.lineHeight};
  }
`;

const Subline = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.fontFamily.default};
  font-size: ${({ theme }) => theme.fontSize.sm.size};
  font-weight: 600;
  line-height: ${({ theme }) => theme.fontSize.sm.lineHeight};
`;

/** From xl, the seals stand at the right of the headline on the stage. */
const SealsOnStage = styled.div`
  display: none;

  ${({ theme }) => theme.mq('xl')} {
    display: block;
    position: absolute;
    right: 0;
    bottom: 0;
  }
`;

/** The search mask. Its lower part reaches below the green area. */
const SearchArea = styled.div`
  margin-top: ${({ theme }) => theme.spacing[40]};
  margin-bottom: calc(-1 * ${searchOverlap.xs});

  ${({ theme }) => theme.mq('md')} {
    margin-top: ${({ theme }) => theme.spacing[50]};
    margin-bottom: calc(-1 * ${searchOverlap.md});
  }

  ${({ theme }) => theme.mq('lg')} {
    margin-bottom: calc(-1 * ${searchOverlap.lg});
  }
`;

/** The content below the green area starts below the search mask. */
const BelowStage = styled.div`
  padding-top: calc(${searchOverlap.xs} + ${({ theme }) => theme.spacing[20]});

  ${({ theme }) => theme.mq('md')} {
    padding-top: calc(
      ${searchOverlap.md} + ${({ theme }) => theme.spacing[20]}
    );
  }

  ${({ theme }) => theme.mq('lg')} {
    padding-top: calc(
      ${searchOverlap.lg} + ${({ theme }) => theme.spacing[20]}
    );
  }
`;

/** The USP badges close the first viewport with some space below. */
const UspArea = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing[50]};
`;

/** Below xl, the seals follow the search mask. */
const SealsBelowSearch = styled.div`
  padding: ${({ theme }) => theme.spacing[40]};

  ${({ theme }) => theme.mq('xl')} {
    display: none;
  }
`;

export {
  BelowStage,
  GreenStage,
  Headline,
  HeadlineRow,
  SealsBelowSearch,
  SearchArea,
  SealsOnStage,
  StageContent,
  Subline,
  Title,
  UspArea,
};
